import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

const categoryNameMap: Record<string, string> = {
  all: '全部',
  romance: '恋爱',
  fantasy: '奇幻',
  action: '动作',
  drama: '剧情',
  comedy: '搞笑',
  thriller: '悬疑',
  daily: '日常',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const activeSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  const filteredComics = useMemo(() => {
    let result = activeSlug === 'all'
      ? [...comics]
      : comics.filter((c) => c.categories.includes(activeSlug));

    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    } else if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else {
      result.sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [activeSlug, sortBy]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类浏览', href: '/category/all' },
    { label: currentCategory?.name || activeSlug },
  ];

  const pageTitle = `${currentCategory?.name || ''}韩国漫画 - 韩漫网`;
  const pageDesc = currentCategory?.description
    ? `${currentCategory.description}。韩漫网提供韩国漫画全集免费在线阅读，下拉式阅读模式让您畅享韩漫。`
    : '韩漫网提供韩国漫画全集免费在线阅读，下拉式阅读模式让您畅享韩漫。';

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: pageTitle,
          description: pageDesc,
          url: `https://www.hanmanwang.com/category/${activeSlug}`,
        })}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          <main>
            <section className="bg-background-100 py-8 md:py-10">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900 mb-2">
                  {currentCategory?.name || '全部'}韩国漫画
                </h1>
                <p className="text-sm text-foreground-500">
                  {currentCategory?.description || '浏览全部韩国漫画，发现你的最爱'}
                </p>
              </div>
            </section>

            {/* Category Tabs */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 -mt-4">
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-1.5 flex items-center flex-wrap gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </section>

            {/* Sort Controls */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-sm text-foreground-500">
                  共找到 <strong className="text-foreground-900">{filteredComics.length}</strong> 部漫画
                </p>
                <div className="flex items-center gap-1 bg-background-100 rounded-lg p-1">
                  {([
                    { key: 'latest', label: '最新' },
                    { key: 'popular', label: '最热' },
                    { key: 'rating', label: '评分' },
                  ] as const).map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSortBy(s.key)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        sortBy === s.key
                          ? 'bg-background-50 text-foreground-900'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Comic Grid */}
            <section className="max-w-7xl mx-auto px-4 md:px-6 pb-14">
              {filteredComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                  {filteredComics.map((comic) => (
                    <ComicCard key={comic.id} {...comic} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                    <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无漫画，请浏览其他分类</p>
                  <Link
                    to="/category/all"
                    className="inline-block mt-4 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap"
                  >
                    返回全部漫画
                  </Link>
                </div>
              )}
            </section>
          </main>
        </div>
        <Footer />
      </div>
    </>
  );
}